import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchSalesData } from '../../../redux/SalesDetailed';
import DateSelector from '../../../commonComponents/DateSelector';
import { useTheme } from '../../../context/ThemeContext';
import {
  SalesSummaryCard,
  SummaryHeader,
  SummaryTitle,
  SummaryDescription,
  TotalSalesRow,
  TotalSalesAmount,
  TrendIndicator,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  DateSelectorWrapper,
  MetricsSection,
  LoadingContainer,
  KeyMetricsTitle,
} from './styles';
import { DateRange, SalesSummaryProps, User, KeyMetric } from './types';
import { getKeyMetrics, getDefaultMetrics } from './constants';

const SalesSummary: React.FC<SalesSummaryProps> = ({ monthlyChange, merchant, storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { salesData } = useSelector((state: RootState) => state.salesDetailed);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment().subtract(7, 'days'),
    endDate: moment(),
  });
  const [keyMetrics, setKeyMetrics] = useState<KeyMetric[]>([]);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const user: User = JSON.parse(localStorage.getItem('user') || '{}');

  const handleSelection = (selection: DateRange) => {
    setSelectedDateRange(selection);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params: any = {
          startDate: moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
          endDate: moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
          merchantId: merchant?.id,
        };
        if (storeId) {
          params.store = storeId;
        }

        await dispatch(fetchSalesData(params)).unwrap();
      } catch (error) {
        console.error('Error fetching sales data:', error);
        setKeyMetrics(getDefaultMetrics());
        setTotalSales(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedDateRange, merchant, storeId, dispatch]);

  useEffect(() => {
    if (salesData) {
      const metrics = getKeyMetrics(salesData);
      setKeyMetrics(metrics);
      
      const netSales = parseFloat(String(salesData.netSales)) || 0;
      setTotalSales(netSales);
    }
  }, [salesData, user]);

  const isIncrease = monthlyChange >= 0;

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <SalesSummaryCard>
        <SummaryHeader>
          <SummaryTitle>Total Sales</SummaryTitle>
          <DateSelectorWrapper>
            <DateSelector onDateSelect={handleSelection} />
          </DateSelectorWrapper>
        </SummaryHeader>
        <SummaryDescription>Detailed insights into your sales performance.</SummaryDescription>
        <TotalSalesRow>
          <TotalSalesAmount>
            ${(totalSales || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </TotalSalesAmount>
          <TrendIndicator isIncrease={isIncrease}>
            <FontAwesomeIcon icon={isIncrease ? faArrowUp : faArrowDown} />
            <span>
              {Math.abs(monthlyChange)}% {isIncrease ? '↑' : '↓'} this month
            </span>
          </TrendIndicator>
        </TotalSalesRow>
        <MetricsSection>
          {isLoading ? (
            <LoadingContainer>
              Loading metrics...
            </LoadingContainer>
          ) : (
            <>
              <KeyMetricsTitle>Key Metrics</KeyMetricsTitle>
              <MetricsGrid>
                {keyMetrics.map((metric) => (
                  <MetricCard key={metric.id}>
                    <MetricHeader>
                      <MetricIcon icon={metric.icon} color={metric.color} />
                      <MetricTitle>{metric.title}</MetricTitle>
                    </MetricHeader>
                    <MetricValue>
                      ${(parseFloat(metric.value) || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </MetricValue>
                  </MetricCard>
                ))}
              </MetricsGrid>
            </>
          )}
        </MetricsSection>
      </SalesSummaryCard>
    </ThemeProvider>
  );
};

export default SalesSummary;
